/**
 * V2 admin charts — hand-rolled SVG, no chart library. Pure functions
 * (data in, markup out) so they're unit-testable and render identically
 * in jsdom and Safari. Colors come from the atlas tokens; every shape is
 * sized in a viewBox so the cards can flex on iPad and phone.
 */
import { escapeHtml } from '../../../assets/js/utils.js';

export const CHART_COLORS = {
  ink: '#3d2f1f',
  inkSoft: '#6b5a42',
  // Series hues. Re-stepped 2026-08-23: the old pair (#1f4a32 / #1f4e79) sat at
  // a normal-vision separation of only ~12 (OKLab ΔE ×100) — both read as "dark
  // ink" on the parchment, and the two lines were reported as indistinguishable.
  // These pass all six palette checks against the --paper surface #fdf8ec:
  // lightness band, chroma floor, CVD ΔE 19.4 (deutan), normal ΔE 20.5, and
  // contrast (white pill text stays >= 4.5:1 on both). Re-validate before
  // changing either one.
  green: '#1f6f45',
  blue: '#2f6fc4',
  red: '#a93226',
  grid: 'rgba(61, 47, 31, 0.12)',
  paper: '#fdf8ec',
};

/**
 * Secondary (non-color) encoding per series, applied in fixed index order and
 * never cycled by rank. Color alone is not identity: these keep the layers
 * separable for colorblind viewers, in grayscale, and — the case that actually
 * bit us — where two translucent fills overlap and wash to the same gray.
 */
export const SERIES_STYLES = [
  { dash: '', marker: 'circle', fillOpacity: 0.16 },
  { dash: '7 4', marker: 'square', fillOpacity: 0.09 },
  { dash: '2 3', marker: 'diamond', fillOpacity: 0.07 },
];

export function seriesStyle(i) {
  return SERIES_STYLES[i] || SERIES_STYLES[SERIES_STYLES.length - 1];
}

/** Round an axis ceiling up to a friendly number (48 → 50, 3 → 4). */
export function niceCeil(max) {
  if (max <= 4) return Math.max(max, 4);
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const step = magnitude / 2;
  return Math.ceil(max / step) * step;
}

function pointsFor(values, w, h, pad, ceil) {
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const step = values.length > 1 ? innerW / (values.length - 1) : 0;
  return values.map((v, i) => ({
    x: pad.l + (values.length > 1 ? i * step : innerW / 2),
    y: pad.t + innerH - (ceil > 0 ? (v / ceil) * innerH : 0),
  }));
}

function lineD(pts) {
  return pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
}

/**
 * One data marker. The shape is the series' non-color identity, so it must
 * survive the chart's `preserveAspectRatio="none"` stretch — a stretched square
 * is still obviously not a circle. `attrs` carries fill/stroke, `title` the
 * hover tooltip.
 */
function marker(shape, p, attrs, title) {
  const x = p.x.toFixed(1);
  const y = p.y.toFixed(1);
  const r = 3.5;
  if (shape === 'square') {
    return `<rect x="${(p.x - r).toFixed(1)}" y="${(p.y - r).toFixed(1)}"
      width="${r * 2}" height="${r * 2}" ${attrs}>${title}</rect>`;
  }
  if (shape === 'diamond') {
    const d = r * 1.25;
    return `<polygon points="${p.x.toFixed(1)},${(p.y - d).toFixed(1)} ${(p.x + d).toFixed(1)},${y}
      ${x},${(p.y + d).toFixed(1)} ${(p.x - d).toFixed(1)},${y}" ${attrs}>${title}</polygon>`;
  }
  return `<circle cx="${x}" cy="${y}" r="${r}" ${attrs}>${title}</circle>`;
}

/**
 * Layered area chart for one-or-more day series.
 * series: [{ values, color, label }], days: [{ label, date }]
 */
export function areaChart(series, days, { w = 660, h = 200 } = {}) {
  if (!days.length || !series.length) return '';
  const pad = { l: 34, r: 10, t: 14, b: 24 };
  const ceil = niceCeil(Math.max(...series.flatMap((s) => s.values), 1));
  const innerH = h - pad.t - pad.b;
  const baseline = pad.t + innerH;

  // Horizontal gridlines at 0 / half / full
  const gridlines = [0, 0.5, 1].map((f) => {
    const y = pad.t + innerH - f * innerH;
    const value = Math.round(ceil * f);
    return `
      <line x1="${pad.l}" y1="${y.toFixed(1)}" x2="${w - pad.r}" y2="${y.toFixed(1)}"
        stroke="${CHART_COLORS.grid}" stroke-width="1"/>
      <text x="${pad.l - 6}" y="${(y + 3).toFixed(1)}" text-anchor="end"
        font-size="10" fill="${CHART_COLORS.inkSoft}">${value}</text>`;
  }).join('');

  // Weekend shading (Sat/Sun columns get a faint wash)
  const step = days.length > 1 ? (w - pad.l - pad.r) / (days.length - 1) : 0;
  const weekends = days.map((d, i) => {
    if (d.label !== 'Sat' && d.label !== 'Sun') return '';
    const x = pad.l + i * step - step / 2;
    return `<rect x="${Math.max(x, pad.l).toFixed(1)}" y="${pad.t}"
      width="${step.toFixed(1)}" height="${innerH}" fill="rgba(61,47,31,0.05)"/>`;
  }).join('');

  // Fills first for EVERY series, then every line and marker on top. Drawing
  // each series as a complete unit meant series 2's translucent fill washed
  // over series 1's line, which is half of why the two read as one muddy band.
  const plotted = series.map((s, i) => ({
    s,
    style: seriesStyle(i),
    pts: pointsFor(s.values, w, h, pad, ceil),
  }));

  const fills = plotted.map(({ s, style, pts }) => {
    const area = `${lineD(pts)} L${pts[pts.length - 1].x.toFixed(1)},${baseline} L${pts[0].x.toFixed(1)},${baseline} Z`;
    return `<path d="${area}" fill="${s.color}" opacity="${style.fillOpacity}"/>`;
  }).join('');

  const strokes = plotted.map(({ s, style, pts }) => {
    const markers = pts.map((p, i) => {
      const title = `<title>${escapeHtml(days[i].label)} ${escapeHtml(days[i].date || '')}: ${s.values[i]} ${escapeHtml(s.label)}</title>`;
      // A paper-colored ring keeps a marker legible where the two series cross.
      const ring = `fill="${s.color}" stroke="${CHART_COLORS.paper}" stroke-width="1.5"`;
      return marker(style.marker, p, ring, title);
    }).join('');
    return `
      <path d="${lineD(pts)}" fill="none" stroke="${s.color}" stroke-width="2"
        stroke-linejoin="round" stroke-linecap="round"
        ${style.dash ? `stroke-dasharray="${style.dash}"` : ''}/>
      ${markers}`;
  }).join('');

  const layers = `${fills}${strokes}`;

  const xLabels = days.map((d, i) => {
    if (days.length > 8 && i % 2 !== days.length % 2) return ''; // every other day when crowded
    const x = pad.l + i * step;
    return `<text x="${x.toFixed(1)}" y="${h - 8}" text-anchor="middle"
      font-size="10" fill="${CHART_COLORS.inkSoft}">${escapeHtml(d.label)}</text>`;
  }).join('');

  return `
    <svg class="admin-chart-svg" viewBox="0 0 ${w} ${h}" role="img"
      preserveAspectRatio="none" aria-label="Daily activity chart">
      ${weekends}${gridlines}${layers}${xLabels}
    </svg>`;
}

/**
 * Legend key for series `i` — the line style and marker, not just a color chip,
 * so the legend states identity the same way the plot does.
 */
export function legendSwatch(color, i = 0) {
  const style = seriesStyle(i);
  return `
    <svg class="admin-legend-swatch" viewBox="0 0 26 14" aria-hidden="true" focusable="false">
      <line x1="1" y1="7" x2="25" y2="7" stroke="${color}" stroke-width="2" stroke-linecap="round"
        ${style.dash ? `stroke-dasharray="${style.dash}"` : ''}/>
      ${marker(style.marker, { x: 13, y: 7 }, `fill="${color}" stroke="${CHART_COLORS.paper}" stroke-width="1.5"`, '')}
    </svg>`;
}

/** Tiny sparkline for a KPI card. */
export function sparkline(values, color = CHART_COLORS.green, { w = 120, h = 30 } = {}) {
  if (!values.length) return '';
  const pad = { l: 2, r: 2, t: 3, b: 3 };
  const ceil = Math.max(...values, 1);
  const pts = pointsFor(values, w, h, pad, ceil);
  return `
    <svg class="admin-sparkline" viewBox="0 0 ${w} ${h}" aria-hidden="true"
      preserveAspectRatio="none">
      <path d="${lineD(pts)}" fill="none" stroke="${color}" stroke-width="2"
        stroke-linejoin="round" stroke-linecap="round" opacity="0.7"/>
    </svg>`;
}

/**
 * Donut gauge for a 0–100 rate. Color communicates health
 * (≥50 green, ≥25 amber, below red — V1's retention thresholds).
 */
export function gauge(rate, label, { size = 120 } = {}) {
  const clamped = Math.max(0, Math.min(100, rate));
  const color = clamped >= 50 ? CHART_COLORS.green : clamped >= 25 ? '#b45309' : CHART_COLORS.red;
  const r = 48;
  const c = 2 * Math.PI * r;
  const filled = (clamped / 100) * c;
  return `
    <svg class="admin-gauge" viewBox="0 0 ${size} ${size}" role="img"
      aria-label="${escapeHtml(label)}: ${clamped}%">
      <circle cx="60" cy="60" r="${r}" fill="none" stroke="${CHART_COLORS.grid}" stroke-width="10"/>
      <circle cx="60" cy="60" r="${r}" fill="none" stroke="${color}" stroke-width="10"
        stroke-dasharray="${filled.toFixed(1)} ${c.toFixed(1)}" stroke-linecap="round"
        transform="rotate(-90 60 60)"/>
      <text x="60" y="58" text-anchor="middle" font-size="26" font-weight="700"
        fill="${CHART_COLORS.ink}">${clamped}%</text>
      <text x="60" y="76" text-anchor="middle" font-size="10"
        fill="${CHART_COLORS.inkSoft}">${escapeHtml(label)}</text>
    </svg>`;
}
